import { Injectable, OnDestroy } from '@angular/core';
import {
	AsyncSubject,
	BehaviorSubject,
	EMPTY,
	NEVER,
	Observable,
	of,
	Subject,
	timer,
} from 'rxjs';
import {
	catchError,
	debounceTime,
	distinctUntilChanged,
	filter,
	finalize,
	map,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs/operators';
import { Permits } from 'src/lib/constants/permissions';
import { getEnvironment } from 'src/lib/environment/environment';
import { RealtimeService } from '../../api/realtime/realtime.service';
import { UserActiveService } from '../../startup/user-active/user-active.service';
import { PermissionStoreService } from '../permission-store/permission-store.service';
import { OnlineStatus } from './online-status.enum';

@Injectable({
	providedIn: 'root',
})
export class OnlineStatusStoreService implements OnDestroy {
	private _unsubscribe$ = new AsyncSubject<null>();
	private _userStateSubjects = new Map<number, BehaviorSubject<OnlineStatus>>();
	private _newUserSubject = new Subject<void>();
	private _pollCompletedSubject = new Subject<void>();

	constructor(
		private realtimeService: RealtimeService,
		private permissionStore: PermissionStoreService,
		private userActiveService: UserActiveService,
	) {
		this._pollCompletedSubject
			.pipe(
				switchMap(() =>
					timer(
						getEnvironment().settings.services.utilities.userActive
							.checkLongPollInterval,
					),
				),
				switchMap(() => this.pollUserStatusesIfPermitted('long-poll')),
				takeUntil(this._unsubscribe$),
			)
			.subscribe();

		timer(0)
			.pipe(
				switchMap(() => this.pollUserStatusesIfPermitted('initial')),
				takeUntil(this._unsubscribe$),
			)
			.subscribe();

		this._newUserSubject
			.pipe(
				debounceTime(
					getEnvironment().settings.services.utilities.userActive
						.checkResponsivePollInterval,
				),
				switchMap(() => this.pollUserStatusesIfPermitted('responsive')),
				takeUntil(this._unsubscribe$),
			)
			.subscribe();

		let waitedLongPoll = false;
		this.userActiveService.tabHidden$
			.pipe(
				switchMap((hidden) => {
					if (hidden) {
						return timer(
							getEnvironment().settings.services.utilities.userActive
								.checkLongPollInterval,
						).pipe(
							tap(() => {
								waitedLongPoll = true;
							}),
							switchMap(() => NEVER),
						);
					} else {
						return of(null);
					}
				}),
				switchMap(() => {
					if (waitedLongPoll) {
						waitedLongPoll = false;
						return this.pollUserStatusesIfPermitted('tab-reveal');
					}
					return EMPTY;
				}),
				takeUntil(this._unsubscribe$),
			)
			.subscribe();

		this.userActiveService.isAway$
			.pipe(
				distinctUntilChanged(),
				switchMap(() => timer(1000)), // Give some time for user to become active again
				filter((isAway) => !isAway),
				switchMap(() => this.pollUserStatusesIfPermitted('away-to-active')),
				takeUntil(this._unsubscribe$),
			)
			.subscribe();
	}

	public userOnlineStatus$ = (uid: number) => {
		return this.getSubject(uid)
			.asObservable()
			.pipe(
				map((v) => (v === undefined ? OnlineStatus.offline : v)),
				distinctUntilChanged(),
			);
	};

	public userOnlineStatusVerified$ = (uid: number) => {
		return this.getSubject(uid)
			.asObservable()
			.pipe(
				filter((x) => x !== undefined),
				distinctUntilChanged(),
			);
	};

	private getSubject = (uid: number) => {
		if (!this._userStateSubjects.has(uid)) {
			const sub = new BehaviorSubject<OnlineStatus>(undefined);
			this._userStateSubjects.set(uid, sub);
			this._newUserSubject.next();
		}

		return this._userStateSubjects.get(uid);
	};

	private pollUserStatuses(
		source:
			| 'long-poll'
			| 'responsive'
			| 'tab-reveal'
			| 'away-to-active'
			| 'initial',
	) {
		const keys = [...this._userStateSubjects.keys()];
		if (keys.length === 0) {
			this._pollCompletedSubject.next();
			return EMPTY;
		}

		if (this.userActiveService.tabHidden) {
			this._pollCompletedSubject.next();
			return EMPTY;
		}

		return this.realtimeService
			.getUserOnlineStatus({
				uids: keys,
			})
			.pipe(
				tap((results) => {
					Object.entries(results).forEach(([uid, status]) => {
						if (this._userStateSubjects.has(Number(uid))) {
							this._userStateSubjects.get(Number(uid)).next(status);
						}
					});
				}),
				catchError((error) => {
					console.error(`Online status polling failed (${source}):`, error);
					return EMPTY;
				}),
				finalize(() => {
					this._pollCompletedSubject.next();
				}),
			);
	}

	private pollUserStatusesIfPermitted(
		source:
			| 'long-poll'
			| 'responsive'
			| 'tab-reveal'
			| 'away-to-active'
			| 'initial',
	): Observable<unknown> {
		return this.permissionStore.getFieldSet$().pipe(
			switchMap((permissions) => {
				if (
					permissions.canDo(
						Permits['ga_direct_message|gabby|participate gabby'],
					)
				) {
					return this.pollUserStatuses(source);
				}

				this._pollCompletedSubject.next();
				return EMPTY;
			}),
		);
	}

	ngOnDestroy() {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}
}
